import { AuthenticationResponse } from "../../profileTypes"
import { Box, Grid, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { Button } from "../../../office/admin/components/Button"
import { useApiMutation } from "../../../../core/api/useApiMutation"
import { sendUserAccountsConfirmationEmail } from "../../profileApi"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { StandardLoadingButton } from "../../../../shared/components/buttons/LoadingButton"

const useStyles = makeStyles()((theme) => ({
    container: {
        maxWidth: "70%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 3rem 3rem 3rem",
    },
    pendingState: {
        padding: "3rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "2rem",
    },
    pendingApproval: {
        fontWeight: 600,
        fontSize: "1.5rem",
    },
    subtitle: {
        textAlign: "center",
        color: theme.palette.tailwind.neutral[600],
    },
    email: {
        marginTop: ".5rem",
        fontWeight: 600,
    },
    button: {
        width: "fit-content",
    },
    pendingApprovalButton: {
        color: "rgba(0, 0, 0, 0.26)",
        width: "fit-content",
    },
    switchDescriptionEmail: {
        color: "#54954b",
    },
}))

export function NoAccessContent({
    userId,
    authenticationResult,
    isEmailConfirmationSend,
    setIsEmailConfirmationSend,
}: {
    userId: number
    authenticationResult: AuthenticationResponse
    isEmailConfirmationSend: boolean
    setIsEmailConfirmationSend: (value: boolean) => void
}) {
    const { classes } = useStyles()
    const { mutateAsync: sendEmail, isLoading } = useApiMutation(sendUserAccountsConfirmationEmail, {})

    const handleSendEmail = async () => {
        await sendEmail({ userId })
        setIsEmailConfirmationSend(true)
    }

    if (isEmailConfirmationSend) {
        return (
            <Box className={classes.pendingState}>
                <LocalizedStrict id="change-user-account-dialog-pending-state-email-has-been-sent">
                    <Typography className={classes.subtitle}>
                        An email has been sent. Please check you email inbox.
                    </Typography>
                </LocalizedStrict>
                <LocalizedStrict
                    id="change-user-account-dialog-pending-state-pending-approval"
                    attrs={{ loadingLabel: true }}
                >
                    <StandardLoadingButton
                        loading
                        loadingLabel="Pending approval..."
                        className={classes.pendingApprovalButton}
                    >
                        Pending approval...
                    </StandardLoadingButton>
                </LocalizedStrict>
            </Box>
        )
    }

    return (
        <Grid container direction="row" alignItems="center" justifyContent="center">
            <Box className={classes.container}>
                <LocalizedStrict
                    id="change-user-account-dialog-confirm-email-subtitle"
                    vars={{ email: authenticationResult?.user.email }}
                    elems={{
                        element: (
                            <span className={classes.switchDescriptionEmail}>{authenticationResult?.user.email}</span>
                        ),
                    }}
                >
                    <Typography variant="body1" textAlign="center">
                        Before you can switch between user accounts, you must first confirm that you own the email
                        account{" "}
                        <span className={classes.switchDescriptionEmail}>{authenticationResult?.user.email}</span>
                    </Typography>
                </LocalizedStrict>
            </Box>
            <Stack direction="column" alignItems="center" spacing="1rem">
                <LocalizedStrict id="change-user-account-dialog-click-below-subtitle">
                    <Typography className={classes.subtitle}>
                        Click on the button below to send me a confirmation link:
                    </Typography>
                </LocalizedStrict>
                <LocalizedStrict id="change-user-account-dialog-send-confirmation-button">
                    <Button
                        onClick={handleSendEmail}
                        disabled={isLoading}
                        className={classes.button}
                        variant="contained"
                    >
                        Send confirmation link
                    </Button>
                </LocalizedStrict>
            </Stack>
        </Grid>
    )
}

